import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { FlaskConical, Check, AlertTriangle, Download, Eye } from 'lucide-react';

const pilotSchools = [
  { name: 'Raffles Girls\' School', level: 'Sec', coordinator: 'Mrs Tan Mei Ling', c1: ['Aug✓', 'Sep✓', 'Oct✓'], c2: ['Nov●', 'Jan○', 'Feb○'], status: 'on_track' },
  { name: 'Nanyang Primary', level: 'Pri', coordinator: 'Mr Loh Beng Huat', c1: ['Aug✓', 'Sep✓', 'Oct✓'], c2: ['Nov●', 'Jan○', 'Feb○'], status: 'on_track' },
  { name: 'Victoria Junior College', level: 'Pre-U', coordinator: 'Ms Priscilla Koh', c1: ['Aug✓', 'Sep✓', 'Oct✓'], c2: ['Nov●', 'Jan○', 'Feb○'], status: 'on_track' },
  { name: 'Queenstown Secondary', level: 'Sec', coordinator: 'Mrs Fatimah Yusof', c1: ['Aug✓', 'Sep✓', 'Oct✓'], c2: ['Nov●', 'Jan○', 'Feb○'], status: 'on_track' },
  { name: 'Punggol Green Primary', level: 'Pri', coordinator: 'Ms Tan Hui Ying', c1: ['Aug✓', 'Sep✓', 'Oct⚠'], c2: ['Nov○'], status: 'issue' },
];

const invoices = [
  { number: 'INV-PILOT-AUG-001', service: 'Acq + Processing + Logistics (5 × 20)', amount: 12480, month: 'Aug 2026', status: 'paid' },
  { number: 'INV-PILOT-SEP-001', service: 'Same', amount: 12480, month: 'Sep 2026', status: 'paid' },
  { number: 'INV-PILOT-OCT-001', service: 'Same', amount: 12480, month: 'Oct 2026', status: 'paid' },
  { number: 'INV-PILOT-CAT-C1', service: 'Cataloguing — Cycle 1 (OCLC at cost)', amount: 4200, month: 'End Cycle 1', status: 'paid' },
  { number: 'INV-PILOT-OCLC', service: 'OCLC registration (one-time)', amount: 1200, month: 'One-time', status: 'paid' },
  { number: 'INV-PILOT-SPINE', service: 'Spine label design (one-time)', amount: 800, month: 'One-time', status: 'paid' },
  { number: 'INV-PILOT-NOV-001', service: 'Acq + Processing + Logistics', amount: 12480, month: 'Nov 2026', status: 'awaiting' },
];

export default function MOEPilotManagement() {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <FlaskConical className="w-7 h-7 text-[#1A3C6E]" />
          CDS Pilot Management
        </h1>
        <p className="text-slate-500 mt-1">Mar 2026 – Feb 2027 | 5 Schools | 600 Books</p>
      </div>

      {/* Pilot Overview */}
      <Card className="bg-gradient-to-r from-[#1A3C6E]/5 to-[#2E5F8A]/5">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Cycle 1</h3>
              <p className="text-sm text-slate-600">Aug–Oct 2026</p>
              <Badge className="mt-2 bg-emerald-100 text-emerald-700">Complete</Badge>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Cycle 2</h3>
              <p className="text-sm text-slate-600">Nov 2026, Jan–Feb 2027</p>
              <Badge className="mt-2 bg-amber-100 text-amber-700">In Progress</Badge>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Parameters</h3>
              <p className="text-sm text-slate-600">20 books/school/month</p>
              <p className="text-sm text-slate-600">All costs invoiced to MOE HQ</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pilot Schools */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Pilot Schools</CardTitle>
          <CardDescription>Delivery status by school</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>School</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Coordinator</TableHead>
                <TableHead>Cycle 1 Deliveries</TableHead>
                <TableHead>Cycle 2 Deliveries</TableHead>
                <TableHead>Overall Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pilotSchools.map((school, idx) => (
                <TableRow key={idx} className="table-row-striped">
                  <TableCell className="font-medium">{school.name}</TableCell>
                  <TableCell>{school.level}</TableCell>
                  <TableCell>{school.coordinator}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {school.c1.map((d, i) => (
                        <Badge key={i} variant="outline" className={
                          d.includes('✓') ? 'bg-emerald-50 text-emerald-700' :
                          d.includes('⚠') ? 'bg-amber-50 text-amber-700' : ''
                        }>
                          {d}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {school.c2.map((d, i) => (
                        <Badge key={i} variant="outline" className={
                          d.includes('●') ? 'bg-amber-50 text-amber-700' : 'bg-slate-50'
                        }>
                          {d}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {school.status === 'on_track' ? (
                      <Badge className="bg-emerald-50 text-emerald-700">On Track</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="text-amber-600">
                        <AlertTriangle className="w-3 h-3 mr-1" /> 1 issue — View
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Issue Detail */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Issue Detail — Punggol Green Oct delivery
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p><strong>Problem:</strong> Wrong school barcode (RGS label on PGP copy). Reported 28 Oct 2026.</p>
            <p><strong>Resolution:</strong> Replacement dispatched 1 Nov 2026.</p>
            <Badge className="bg-emerald-100 text-emerald-700">✓ Resolved</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Delivery Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Delivery Grid</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead></TableHead>
                  <TableHead>Aug 2026</TableHead>
                  <TableHead>Sep 2026</TableHead>
                  <TableHead>Oct 2026</TableHead>
                  <TableHead>Nov 2026</TableHead>
                  <TableHead>Jan 2027</TableHead>
                  <TableHead>Feb 2027</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pilotSchools.slice(0, 4).map((school, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{school.name.split(' ')[0]}</TableCell>
                    <TableCell><Badge className="bg-emerald-50 text-emerald-700">✓ 20</Badge></TableCell>
                    <TableCell><Badge className="bg-emerald-50 text-emerald-700">✓ 20</Badge></TableCell>
                    <TableCell><Badge className="bg-emerald-50 text-emerald-700">✓ 20</Badge></TableCell>
                    <TableCell><Badge className="bg-amber-50 text-amber-700">● Sched</Badge></TableCell>
                    <TableCell><Badge variant="outline">○</Badge></TableCell>
                    <TableCell><Badge variant="outline">○</Badge></TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-medium">Punggol</TableCell>
                  <TableCell><Badge className="bg-emerald-50 text-emerald-700">✓ 20</Badge></TableCell>
                  <TableCell><Badge className="bg-emerald-50 text-emerald-700">✓ 20</Badge></TableCell>
                  <TableCell><Badge className="bg-amber-50 text-amber-700">⚠ 20</Badge></TableCell>
                  <TableCell><Badge variant="outline">○ TBD</Badge></TableCell>
                  <TableCell><Badge variant="outline">○</Badge></TableCell>
                  <TableCell><Badge variant="outline">○</Badge></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pilot Invoicing */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading">Pilot Invoicing</CardTitle>
            <CardDescription>All invoices to MOE HQ</CardDescription>
          </div>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Month</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv, idx) => (
                <TableRow key={idx} className="table-row-striped">
                  <TableCell className="font-medium">{inv.number}</TableCell>
                  <TableCell>{inv.service}</TableCell>
                  <TableCell>${inv.amount.toLocaleString()}</TableCell>
                  <TableCell>{inv.month}</TableCell>
                  <TableCell>
                    {inv.status === 'paid' ? (
                      <Badge className="bg-emerald-50 text-emerald-700">✓ Paid</Badge>
                    ) : (
                      <Badge className="bg-amber-50 text-amber-700">⏳ Awaiting ack</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Feedback Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Feedback Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-600 italic">
            "RFID tagging functional. Spine labels legible. MTL books well-received. Catalogue records uploaded successfully."
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline">View Full Pilot Feedback</Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export Pilot Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
